import SessionStatusCodes from '../constants/SessionStatusCodes';
import i18n from '../i18n/i18n';

/**
 * Reads the server's error code off a rejected `api.ts` call.
 *
 * `api.ts` throws the Response itself, and its shape depends on the transport:
 * RESTSocket puts the already-parsed payload on `body`, while native `fetch`
 * puts a ReadableStream there and only exposes the payload through `json()`.
 */
export const getServerErrorCode = async (err: any): Promise<string | null> => {
  if (!err) {
    return null;
  }

  if (err.body && typeof err.body === 'object' && typeof err.body.message === 'string') {
    return err.body.message;
  }

  if (typeof err.json === 'function' && !err.bodyUsed) {
    try {
      const data = await err.json();
      return typeof data?.message === 'string' ? data.message : null;
    } catch (e) {
      return null;
    }
  }

  return null;
};

const CUSTOMER_ERROR_KEYS: { [code: string]: string } = {
  EMAIL_ALREADY_REGISTERED: 'email_already_exists',
  PASSWORD_TOO_SIMPLE: 'password_too_simple',
};

/**
 * Turns a rejected `api.ts` call into a message that can be shown to a customer.
 * Add new server codes to `CUSTOMER_ERROR_KEYS` as other forms adopt this.
 */
export const formatServerError = async (err: any): Promise<string> => {
  const code = await getServerErrorCode(err);

  if (code && CUSTOMER_ERROR_KEYS[code]) {
    return i18n.t(CUSTOMER_ERROR_KEYS[code]) as string;
  }

  if (code) {
    // Joi validation text and other raw server messages are English and expose
    // internal field names — keep them out of the UI but visible to developers.
    // eslint-disable-next-line no-console
    console.error(`[api] unmapped server error: ${code}`);
    return i18n.t('invalid_data') as string;
  }

  return i18n.t('error_occured_try_later') as string;
};

export const formatSessionStatus = (statusCode: SessionStatusCodes = 0): string => {
  switch (statusCode) {
    case SessionStatusCodes.FAILED:
    case SessionStatusCodes.FAILED_VENDING_MACHINE_NOT_CONFIGURED:
    case SessionStatusCodes.FAILED_VENDING_MACHINE_PROTOCOL_TIMEOUT:
    default:
      return i18n.t("error_occured");

    case SessionStatusCodes.FAILED_UNABLE_TO_CONNECT:
      return i18n.t("error_connecting_to_machine");

    case SessionStatusCodes.DENIED:
    case SessionStatusCodes.DENIED_VENDING_MACHINE_HAS_CREDIT:
    case SessionStatusCodes.DENIED_VENDING_MACHINE_HAS_ACTIVE_SESSION:
      return i18n.t("machine_in_use");

    case SessionStatusCodes.DENIED_WALLET_IN_USE:
      return i18n.t("wallet_in_use");

    case SessionStatusCodes.DENIED_INSUFFICIENT_FUNDS:
    case SessionStatusCodes.DENIED_DIFFERENT_CURRENCY:
      return i18n.t("insufficient_balance");

    case SessionStatusCodes.DENIED_UNDER_MIN_GATEWAY_AMOUNT:
      return i18n.t("credit_less_than_minimum");

    case SessionStatusCodes.DENIED_OVER_MAX_GATEWAY_AMOUNT:
      return i18n.t("credit_greater_than_maximum");

    case SessionStatusCodes.DENIED_GATEWAY_IS_INACTIVE:
      return i18n.t("invalid_payment_method");

    case SessionStatusCodes.REQUESTED:
      return i18n.t("connecting");

    case SessionStatusCodes.ESTABLISHED:
      return i18n.t("connected");

    case SessionStatusCodes.TERMINATION_REQUESTED:
      return i18n.t("disconnecting");

    case SessionStatusCodes.TERMINATED:
    case SessionStatusCodes.TERMINATED_SERVER_INITIATED:
      return i18n.t("session_finished");

    case SessionStatusCodes.TERMINATED_VMU_TIMEOUT:
      return i18n.t("session_expired");

    case SessionStatusCodes.TERMINATED_VMU_PROTOCOL_RESET:
    case SessionStatusCodes.TERMINATED_VMU_PROTOCOL_UNABLE_TO_INITIATE:
    case SessionStatusCodes.TERMINATED_VMU_PROTOCOL_STATE_IS_INVALID:
    case SessionStatusCodes.TERMINATED_VMU_DISCONNECTED_FROM_HUB:
    case SessionStatusCodes.TERMINATED_VMU_DISCONNECTED_FROM_SERVER:
    case SessionStatusCodes.TERMINATED_VMU_APPLICATION_ERROR:
      return i18n.t("error_occured");
  }
};
